import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import DOMPurify from "dompurify";
import { Newspaper, Check, Crown, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Digest() {
  const [digests, setDigests] = useState([]);
  const [subscribed, setSubscribed] = useState(false);
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedDigest, setSelectedDigest] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get('/digest');
        setDigests(res.data.digests);
        setSubscribed(res.data.subscribed);
        setTier(res.data.tier);
      } catch (err) { /* silent */ }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSubscribe = async (subTier) => {
    setActionLoading(true);
    try {
      await apiClient.post('/digest/subscribe', { tier: subTier });
      setSubscribed(true);
      setTier(subTier);
    } catch (err) { /* silent */ }
    finally { setActionLoading(false); }
  };

  const handleUnsubscribe = async () => {
    setActionLoading(true);
    try {
      await apiClient.post('/digest/unsubscribe');
      setSubscribed(false);
      setTier(null);
    } catch (err) { /* silent */ }
    finally { setActionLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="digest-page">
        <div className="mb-8">
          <p className="overline-text mb-2">Newsletter</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-2">SMAHOO ME.WELL Digest</h1>
          <p className="text-spa-text-secondary">Your curated wellness insights delivered regularly</p>
        </div>

        {/* Subscription Status */}
        {!loading && (
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className={`spa-card relative overflow-hidden ${tier === "basic" ? "ring-2 ring-spa-sage" : ""}`}>
              {tier === "basic" && <div className="absolute top-3 right-3"><Check className="w-5 h-5 text-spa-sage" /></div>}
              <div className="w-12 h-12 bg-spa-sage/10 rounded-2xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-spa-sage" />
              </div>
              <h3 className="font-serif text-xl font-medium text-spa-moss mb-2">Basic Digest</h3>
              <p className="text-spa-text-secondary text-sm mb-4">Monthly wellness tips, community highlights, and event announcements.</p>
              <p className="text-spa-moss font-serif text-2xl mb-4">Free</p>
              {!subscribed ? (
                <Button onClick={() => handleSubscribe("basic")} disabled={actionLoading} className="spa-button-primary w-full" data-testid="subscribe-basic">
                  Subscribe Free
                </Button>
              ) : tier === "basic" ? (
                <Button onClick={handleUnsubscribe} disabled={actionLoading} variant="outline" className="w-full border-spa-border rounded-full" data-testid="unsubscribe">
                  Unsubscribe
                </Button>
              ) : (
                <Button onClick={() => handleSubscribe("basic")} disabled={actionLoading} variant="outline" className="w-full border-spa-border rounded-full" data-testid="switch-basic">
                  Switch to Basic
                </Button>
              )}
            </div>

            <div className={`spa-card relative overflow-hidden ${tier === "premium" ? "ring-2 ring-spa-terracotta" : ""}`}>
              {tier === "premium" && <div className="absolute top-3 right-3"><Check className="w-5 h-5 text-spa-terracotta" /></div>}
              <div className="w-12 h-12 bg-spa-terracotta/10 rounded-2xl flex items-center justify-center mb-4">
                <Crown className="w-6 h-6 text-spa-terracotta" />
              </div>
              <h3 className="font-serif text-xl font-medium text-spa-moss mb-2">Premium Digest</h3>
              <p className="text-spa-text-secondary text-sm mb-4">Everything in Basic plus exclusive content, early event access, and wellness guides from Mr. RAPsoul.R.</p>
              <p className="text-spa-moss font-serif text-2xl mb-4">Premium</p>
              {tier !== "premium" ? (
                <Button onClick={() => handleSubscribe("premium")} disabled={actionLoading} className="spa-button-primary w-full" data-testid="subscribe-premium">
                  Upgrade to Premium
                </Button>
              ) : (
                <Button onClick={handleUnsubscribe} disabled={actionLoading} variant="outline" className="w-full border-spa-border rounded-full" data-testid="unsubscribe-premium">
                  Unsubscribe
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Newsletter Archive */}
        <h2 className="font-serif text-xl font-medium text-spa-moss mb-4">Newsletter Archive</h2>
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="w-8 h-8 border-2 border-spa-terracotta border-t-transparent rounded-full animate-spin" /></div>
        ) : digests.length > 0 ? (
          <div className="space-y-4">
            {digests.map(d => (
              <div key={d.digest_id} className="spa-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedDigest(selectedDigest?.digest_id === d.digest_id ? null : d)} data-testid={`digest-${d.digest_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${d.tier === "premium" ? "bg-spa-terracotta/10 text-spa-terracotta" : "bg-spa-sage/10 text-spa-sage"}`}>{d.tier}</span>
                    <span className="text-spa-text-secondary text-xs">{new Date(d.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <h3 className="font-serif text-lg font-medium text-spa-moss mb-1">{d.title}</h3>
                {selectedDigest?.digest_id === d.digest_id && (
                  <div className="mt-4 pt-4 border-t border-spa-border prose prose-stone prose-sm max-w-none text-spa-text-secondary" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.content) }} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="spa-card p-8 text-center">
            <Newspaper className="w-10 h-10 text-spa-border mx-auto mb-3" />
            <p className="text-spa-text-secondary">No newsletters yet. Subscribe to get notified!</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
